
import { computed, defineComponent, reactive, ref, Ref, watch } from "vue";
import InputText from "@/components/primevueCustom/InputText.vue";
import Button from "primevue/button";
import { Field, Form, ErrorMessage } from "vee-validate";
import $API from "@/services";

import { ISignupInfo, IResendEmailInfo } from "@/models/sign";
import { ExceptionMessages, IAlert, RouterName, SEX } from "@/models/common";
import InputMask from "primevue/inputmask";
import { useI18n } from "vue-i18n";
import { messageType } from "@/models/message";
import { useStore } from "@/store";
import { CommonMutationTypes } from "@/store/modules/common/mutations";
import { PossibleEmail } from "@/models/account";
import router from "@/router";
import Dropdown from "primevue/dropdown";
import { useConfirm } from "primevue/useconfirm";
import CustomConfirmDialog from "@/components/primevueCustom/ConfirmDialog.vue";

export default defineComponent({
  name: "Signup Form",
  emits: ["form-status"],
  setup(props, { emit }) {
    const { t } = useI18n({ useScope: "global" });
    const state = reactive({
      step: 0,
      isPossibleEmail: false,
      isPossibleEmailButton: false,
    });

    const email = ref({
      emailName: "",
      emailProvider: "",
    });

    const selectedEmailProvider = ref({ name: t("direct input"), code: "self" });
    const emailProviders = ref([
      { name: t("direct input"), code: "self" },
      //{ name: "naver.com", code: "naver.com" },
      //{ name: "hanmail.net", code: "hanmail.net" },
      //{ name: "nate.conm", code: "nate.com" },
      { name: "gmail.com", code: "gmail.com" },
      //{ name: "kakao.com", code: "kakao.com" },
    ]);
    const isSelf = ref(true);

    watch(
      () => selectedEmailProvider.value,
      (n) => {
        if (n.code === "self") {
          email.value.emailProvider = "";
          isSelf.value = true;
        } else {
          email.value.emailProvider = n.code;
          isSelf.value = false;
        }
      }
    );

    // const error = ref("");
    const alert: Ref<IAlert> = ref({
      title: t("signup"),
      type: messageType.ERROR,
      content: "",
    });

    const inputData = reactive({
      password: "",
      passwordConfirm: "",
      familyName: "",
      firstName: "",
      email: computed(() => `${email.value.emailName}@${email.value.emailProvider}`),
      birthDate: "",
      sex: "",
      mobilePhone: "",
    });

    const isValidFormData = computed(() => {
      return (
        state.isPossibleEmail &&
        inputData.email?.length > 0 &&
        inputData.familyName?.length > 0 &&
        inputData.password?.length > 0 &&
        inputData.passwordConfirm?.length > 0 &&
        inputData.password == inputData.passwordConfirm &&
        inputData.birthDate?.length > 0 &&
        inputData.sex?.length > 0 &&
        inputData.mobilePhone?.length > 0
      );
    });

    watch(
      () => inputData.email,
      () => {
        const reg =
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          // eslint-disable-next-line no-useless-escape
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (inputData.email.match(reg)) {
          setTimeout(() => {
            const $emailError = document.querySelector(".form__email .-error");
            if (!inputData.email) state.isPossibleEmailButton = false;
            else if ($emailError) state.isPossibleEmailButton = false;
            else state.isPossibleEmailButton = true;
          }, 10);

          if (state.isPossibleEmail) state.isPossibleEmail = false;
        }
      }
    );
    watch(isValidFormData, (newVal: boolean) => {
      emit("form-status", newVal);
    });

    const store = useStore();

    const checkEmail = async () => {
      store.commit(CommonMutationTypes.SET_IS_LODING, true);
      try {
        const checkPossible = await $API.SignService.checkPossibleEmail(inputData.email);
        if (checkPossible === PossibleEmail.Possible) {
          alert.value.content = t("available email");
          alert.value.type = messageType.INFO;
          state.isPossibleEmail = true;
        } else {
          alert.value.content = t("email already in use");
          alert.value.type = messageType.ERROR;
          state.isPossibleEmail = false;
        }
      } catch (ex) {
        console.error(ex);
        alert.value.content = t("error occured. please retry later");
        alert.value.type = messageType.ERROR;
        return;
      } finally {
        store.commit(CommonMutationTypes.SET_IS_LODING, false);
      }
    };

    const signup = async () => {
      if (!state.isPossibleEmail) {
        alert.value = {
          content: t("check email verificaion"),
          type: messageType.ERROR,
        };

        return;
      }
      try {
        store.commit(CommonMutationTypes.SET_IS_LODING, true);
        const params: ISignupInfo = {
          Email: inputData.email,
          Password: inputData.password,
          FirstName: inputData.firstName,
          LastName: inputData.familyName,
          CountryCode: "ko",
          BirthDate: inputData.birthDate,
          Sex: inputData.sex,
          MobilePhone: inputData.mobilePhone,
        };

        const success = await $API.SignService.signup(params);
        if (success) {
          store.commit(CommonMutationTypes.SET_IS_LODING, false);
          signupSuccess();
        }
      } catch (e) {
        if (e.Exception === ExceptionMessages.FAILED) {
          // this.error = data.ResultData.toString();
          alert.value.content = e.ResultData.toString();
          alert.value.type = messageType.ERROR;
        }

        store.commit(CommonMutationTypes.SET_IS_LODING, false);
      }
    };

    const nextStep = () => {
      state.step += 1;
    };

    const confirm = useConfirm();
    const signupSuccess = () => {
      confirm.require({
        header: t("sent confirming email"),
        message: t("finished sign up"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: t("ok"),
        group: "emailSend",
        accept: () => {},
        reject: () => {
          const path = {
            name: RouterName.HOME,
          };
          router.push(path);
        },
      });
    };

    const onResendEmail = async () => {
      try {
        store.commit(CommonMutationTypes.SET_IS_LODING, true);

        // eslint-disable-next-line no-undef
        const params: IResendEmailInfo = {
          Email: inputData.email,
          Password: inputData.password,
        };

        const success = await $API.SignService.resendEmail(params);
        if (success) {
          store.commit(CommonMutationTypes.SET_IS_LODING, false);
          alert.value.content = t("sent verification email");
          alert.value.type = messageType.INFO;
        }

        store.commit(CommonMutationTypes.SET_IS_LODING, false);
      } catch (e) {
        if (e.Exception === ExceptionMessages.FAILED) {
          // this.error = data.ResultData.toString();
          alert.value.content = e.ResultData.toString();
          alert.value.type = messageType.ERROR;
        }

        store.commit(CommonMutationTypes.SET_IS_LODING, false);
      }
    };

    return {
      state,
      inputData,
      alert,
      t,
      store,
      isValidFormData,
      checkEmail,
      signup,
      nextStep,
      onResendEmail,
      email,
      selectedEmailProvider,
      emailProviders,
      isSelf,
      SEX,

      signupSuccess,
    };
  },

  components: {
    InputText,
    Button,
    Field,
    Form,
    ErrorMessage,
    Dropdown,
    InputMask,
    CustomConfirmDialog,
  },
});
